import { useState } from 'react'
import { useAuthStore } from '../../store/auth'
import { useToast } from '../ui/Toast'

interface AsBuiltExportModalProps {
  isOpen: boolean
  onClose: () => void
  siteId: string
  siteName: string
  tenantName?: string
}

const SECTIONS = [
  { id: 'summary', label: 'System Summary', defaultChecked: true },
  { id: 'floorplans', label: 'Floor Plans', defaultChecked: true },
  { id: 'devices', label: 'Device Inventory', defaultChecked: true },
  { id: 'racks', label: 'Rack Elevations', defaultChecked: true },
  { id: 'network', label: 'Network Map', defaultChecked: true },
  { id: 'cables', label: 'Cable Schedule', defaultChecked: true },
  { id: 'poe', label: 'PoE Budget', defaultChecked: true },
  { id: 'warranty', label: 'Warranty Information', defaultChecked: true },
  { id: 'credentials', label: 'Device Credentials (CONFIDENTIAL)', defaultChecked: false },
  { id: 'licenses', label: 'License Summary', defaultChecked: true },
] as const

export function AsBuiltExportModal({ isOpen, onClose, siteId, siteName }: AsBuiltExportModalProps) {
  const { toastError } = useToast()
  const [selectedSections, setSelectedSections] = useState<Set<string>>(
    new Set(SECTIONS.filter(s => s.defaultChecked).map(s => s.id))
  )
  const [generating, setGenerating] = useState(false)

  const toggleSection = (id: string) => {
    setSelectedSections(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const selectAll = () => {
    setSelectedSections(new Set(SECTIONS.map(s => s.id)))
  }

  const deselectAll = () => {
    setSelectedSections(new Set())
  }

  const handleGenerate = async () => {
    if (selectedSections.size === 0) return
    setGenerating(true)

    try {
      const token = useAuthStore.getState().token
      const includeCredentials = selectedSections.has('credentials')
      const sectionList = Array.from(selectedSections).filter(s => s !== 'credentials')

      const params = new URLSearchParams({
        include: sectionList.join(','),
        includeCredentials: String(includeCredentials),
      })

      const res = await fetch(`/api/sites/${siteId}/export/as-built-pdf?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Failed to generate PDF' })) as { error?: string }
        throw new Error(err.error || 'Failed to generate PDF')
      }

      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${siteName}-as-built.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      onClose()
    } catch (err: unknown) {
      toastError('Failed to generate PDF', err)
    } finally {
      setGenerating(false)
    }
  }

  if (!isOpen) return null

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--text)' }}>
            Generate As-Built Document
          </h3>
          <button onClick={onClose} style={closeBtnStyle}>&times;</button>
        </div>

        {/* Content */}
        <div style={{ padding: '0 24px 16px', flex: 1, overflowY: 'auto' }}>
          {/* Section Selection */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>
                Include sections:
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={selectAll} style={linkBtnStyle}>Select all</button>
                <button onClick={deselectAll} style={linkBtnStyle}>Deselect all</button>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {SECTIONS.map((section) => {
                const isConfidential = section.id === 'credentials'
                return (
                  <label
                    key={section.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '8px 12px',
                      borderRadius: 'var(--radius, 8px)',
                      cursor: 'pointer',
                      background: selectedSections.has(section.id)
                        ? isConfidential ? 'var(--err-m)' : 'var(--bg-inset)'
                        : 'transparent',
                      border: isConfidential && selectedSections.has(section.id)
                        ? '1px solid var(--err-m)' : '1px solid transparent',
                      transition: 'all 0.15s',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedSections.has(section.id)}
                      onChange={() => toggleSection(section.id)}
                      style={{ accentColor: isConfidential ? 'var(--err)' : 'var(--primary)', width: 16, height: 16 }}
                    />
                    <span style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: isConfidential ? 'var(--err)' : 'var(--text)',
                    }}>
                      {section.label}
                    </span>
                  </label>
                )
              })}
            </div>
          </div>

          {/* Credentials warning */}
          {selectedSections.has('credentials') && (
            <div style={{
              padding: '10px 14px',
              background: 'var(--err-m)',
              border: '1px solid var(--err-m)',
              borderRadius: 'var(--radius, 8px)',
              marginBottom: 12,
            }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--err)', marginBottom: 2 }}>
                Warning: Credentials Included
              </div>
              <div style={{ fontSize: 11, color: 'var(--err)' }}>
                This document will contain sensitive device credentials. Handle with appropriate security precautions.
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <button onClick={onClose} className="btn btn-secondary">Cancel</button>
          <button
            onClick={handleGenerate}
            disabled={generating || selectedSections.size === 0}
            style={{
              ...generateBtnStyle,
              opacity: generating || selectedSections.size === 0 ? 0.6 : 1,
              cursor: generating || selectedSections.size === 0 ? 'not-allowed' : 'pointer',
            }}
          >
            {generating ? 'Generating...' : 'Generate PDF'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Styles ───

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0,
  background: 'var(--bg-overlay, rgba(0,0,0,0.5))',
  backdropFilter: 'blur(4px)',
  zIndex: 100, display: 'flex',
  alignItems: 'center', justifyContent: 'center',
  padding: 24,
}

const modalStyle: React.CSSProperties = {
  background: 'var(--bg-surface)',
  borderRadius: 'var(--radius-xl, 16px)',
  boxShadow: 'var(--shadow-lg)',
  maxWidth: 480, width: '100%',
  maxHeight: '85vh',
  display: 'flex', flexDirection: 'column',
  animation: 'modal-in 0.2s ease',
}

const headerStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '20px 24px 12px',
}

const closeBtnStyle: React.CSSProperties = {
  background: 'none', border: 'none', fontSize: 22,
  color: 'var(--text-tertiary)', cursor: 'pointer',
  width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
  borderRadius: 'var(--radius, 8px)',
}

const linkBtnStyle: React.CSSProperties = {
  background: 'none', border: 'none',
  fontSize: 11, fontWeight: 500,
  color: 'var(--primary)', cursor: 'pointer',
  padding: '2px 4px',
}

const footerStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
  gap: 10, padding: '12px 24px 20px',
  borderTop: '1px solid var(--border-light)',
}

const cancelBtnStyle: React.CSSProperties = {
  padding: '8px 18px', border: '1px solid var(--border)',
  borderRadius: 'var(--radius, 8px)', background: 'var(--bg-surface)',
  fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)',
  cursor: 'pointer',
}

const generateBtnStyle: React.CSSProperties = {
  padding: '8px 22px', border: 'none',
  borderRadius: 'var(--radius, 8px)', background: 'var(--primary)',
  fontSize: 13, fontWeight: 600, color: '#fff',
}
